import { Box, Grid, Stack, Tooltip, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import { LocationModel } from "object-models/location-model";
import { DataItem } from "../Data/PortfolioViewData";
import { barData, buildEntityEmissions, EntityEmissionData } from "../Data/dataUtils";
import type { AssessmentViewMode } from "../Data/PortfolioViewData";

interface EmissionByScopeCategoryBarProps {
  data: LocationModel;
  assessmentView: AssessmentViewMode;
  rawRows: Array<Partial<LocationModel>>;
  organizationNameById?: Record<number, string>;
  selectedScope?: number[];
  isOrganizationPortfolio?: boolean;
  displayContext?: "portfolio" | "location";
}


/**
 * A React functional component that renders a bar chart showing emissions categorized by various activities.
 * The chart dynamically updates based on the emission data passed.
 *
 * @component
 * @param {Object} props - Props passed to the component.
 * @param {LocationModel} props.data - The location emission data containing values for different categories of emissions.
 */
const EmissionByScopeCategoryBar: React.FC<EmissionByScopeCategoryBarProps> = ({
  data,
  assessmentView,
  rawRows,
  organizationNameById = {},
  selectedScope = [0, 1, 2],
  isOrganizationPortfolio = false,
  displayContext = "portfolio",
}) => {
  const [barChartData, setBarChartData] = useState([] as Array<DataItem>);
  const [entityData, setEntityData] = useState(
    [] as Array<EntityEmissionData>
  );
  const scopeColors = {
    scope1: "#ed9808",
    scope2: "#c43b2e",
    scope3: "#23b3a7",
  };

  /**
   * Generates and returns the data for the bar chart based on the emission data.
   */
  const dataItems = barData(data);

  /**
   * Reshapes the bar chart data by calling `barData` and updating the state.
   *
   * @function
   */
  function reshapeData() {
    if (assessmentView === "organization") {
      const entityItems = buildEntityEmissions(
        rawRows,
        selectedScope,
        isOrganizationPortfolio,
        organizationNameById,
      );
      setEntityData(entityItems);
      setBarChartData(entityItems.map((item) => ({ name: item.name, value: item.total })));
      return;
    }

    setEntityData([]);
    setBarChartData(dataItems);
  }

  // Update bar chart data when `data` changes
  useEffect(() => {
    reshapeData();
  }, [assessmentView, data, rawRows, selectedScope, isOrganizationPortfolio]);

  const activeBars = barChartData
    .filter((item) => Number(item.value) > 0)
    .sort((left, right) => right.value - left.value);
  const activeBarsTotal = activeBars.reduce(
    (sum, current) => sum + current.value,
    0
  );

  const maxValue = activeBars[0]?.value ?? 1;

  const formatCompact = (value: number): string => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${Math.round(value / 1000)}k`;
    }
    return value.toLocaleString("en-IN");
  };

  const axisTicks =
    assessmentView === "organization"
      ? [0, 0.25, 0.5, 0.75, 1].map((ratio) => Math.round(maxValue * ratio))
      : [];

  let chartTitle: string;
  if (assessmentView === "organization") {
    chartTitle = isOrganizationPortfolio
      ? "Emissions by Scope per Location"
      : "Emissions by Scope per Organization";
  } else {
    chartTitle = "Scope 3 Emissions by Category";
  }

  const isLocationScopeView =
    displayContext === "location" && assessmentView !== "organization";
  const layoutStackSpacing = isLocationScopeView ? 1.35 : 2;
  const layoutStackPaddingX = isLocationScopeView
    ? { xs: 0.75, md: 1.25 }
    : { xs: 1, md: 2 };
  const layoutStackPaddingTop = isLocationScopeView
    ? { xs: 0.75, md: 1.25 }
    : { xs: 1, md: 2 };
  const layoutStackPaddingBottom = isLocationScopeView ? 1 : 1.5;

  return (
    <Grid item xs={12} md={6.5} sx={{ width: "100%", maxWidth: "100%" }}>
      <Stack
        spacing={layoutStackSpacing}
        sx={{
          px: layoutStackPaddingX,
          pt: layoutStackPaddingTop,
          pb: layoutStackPaddingBottom,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            textAlign: "center",
            color: "#3a4d67",
            fontWeight: 450,
            fontSize: isLocationScopeView ? "0.68rem" : "0.80rem",
            whiteSpace: "nowrap",
          }}
        >
          {chartTitle}
        </Typography>

        {assessmentView === "organization" && (
          <Box sx={{ display: "flex", justifyContent: "center", gap: 2.5, mt: -0.6 }}>
            {selectedScope.includes(0) && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: scopeColors.scope1 }} />
                <Typography sx={{ fontSize: "0.85rem", color: "#2a3f57" }}>Scope 1</Typography>
              </Box>
            )}
            {selectedScope.includes(1) && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: scopeColors.scope2 }} />
                <Typography sx={{ fontSize: "0.85rem", color: "#2a3f57" }}>Scope 2</Typography>
              </Box>
            )}
            {selectedScope.includes(2) && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: scopeColors.scope3 }} />
                <Typography sx={{ fontSize: "0.85rem", color: "#2a3f57" }}>Scope 3</Typography>
              </Box>
            )}
          </Box>
        )}

        <Box
          sx={{
            maxHeight: isLocationScopeView ? { xs: 240, md: 320 } : { xs: 310, md: 430 },
            overflowY: "auto",
            pr: 0.8,
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": { backgroundColor: "#eef2f5", borderRadius: "10px" },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "#98a4ad", borderRadius: "10px" },
          }}
        >
          <Stack spacing={1.1}>
            {assessmentView === "organization" &&
              entityData.map((item) => {
                const widthRatio = Math.max(2, (item.total / maxValue) * 100);
                const scope1Ratio = item.total > 0 ? (item.scope1 / item.total) * 100 : 0;
                const scope2Ratio = item.total > 0 ? (item.scope2 / item.total) * 100 : 0;
                const scope3Ratio = item.total > 0 ? (item.scope3 / item.total) * 100 : 0;

                return (
                  <Box
                    key={item.name}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "110px 1fr auto",
                      alignItems: "center",
                      columnGap: 1.2,
                    }}
                  >
                    <Typography
                      title={item.name}
                      sx={{
                        color: "#233b58",
                        fontSize: "0.84rem",
                        lineHeight: 1.2,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        wordBreak: "break-word",
                      }}
                    >
                      {item.name}
                    </Typography>

                    <Tooltip
                      title={
                        <Box sx={{ minWidth: 170, py: 0.25 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", mb: 0.6 }}>
                            {item.name}
                          </Typography>
                          <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto", rowGap: 0.35, columnGap: 1.4 }}>
                            <Typography sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: scopeColors.scope1 }} />
                              Scope 1
                            </Typography>
                            <Typography sx={{ fontWeight: 700 }}>{item.scope1.toLocaleString("en-IN")}</Typography>
                            <Typography sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: scopeColors.scope2 }} />
                              Scope 2
                            </Typography>
                            <Typography sx={{ fontWeight: 700 }}>{item.scope2.toLocaleString("en-IN")}</Typography>
                            <Typography sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: scopeColors.scope3 }} />
                              Scope 3
                            </Typography>
                            <Typography sx={{ fontWeight: 700 }}>{item.scope3.toLocaleString("en-IN")}</Typography>
                          </Box>
                          <Box sx={{ mt: 0.65, pt: 0.55, borderTop: "1px solid rgba(255,255,255,0.28)", display: "grid", gridTemplateColumns: "1fr auto" }}>
                            <Typography sx={{ fontWeight: 700 }}>Total</Typography>
                            <Typography sx={{ fontWeight: 700 }}>{item.total.toLocaleString("en-IN")}</Typography>
                          </Box>
                        </Box>
                      }
                      placement="top"
                      arrow
                    >
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          minWidth: 8,
                          height: 40,
                          borderRadius: 0.8,
                          overflow: "hidden",
                          backgroundColor: "#edf1f4",
                          boxShadow: "inset 0 0 0 1px #dbe2e9",
                        }}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            bottom: 0,
                            width: `${widthRatio}%`,
                            display: "flex",
                          }}
                        >
                          <Box sx={{ width: `${scope1Ratio}%`, backgroundColor: scopeColors.scope1 }} />
                          <Box sx={{ width: `${scope2Ratio}%`, backgroundColor: scopeColors.scope2 }} />
                          <Box sx={{ width: `${scope3Ratio}%`, backgroundColor: scopeColors.scope3 }} />
                        </Box>
                      </Box>
                    </Tooltip>

                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: "#0f2b52",
                        minWidth: 64,
                        textAlign: "right",
                        fontSize: "0.86rem",
                      }}
                    >
                      {formatCompact(item.total)}
                    </Typography>
                  </Box>
                );
              })}

            {assessmentView === "organization" && (
              <Box sx={{ display: "grid", gridTemplateColumns: "110px 1fr auto", alignItems: "center", columnGap: 1.2, pt: 0.3 }}>
                <Box />
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", color: "#8796a8", fontSize: "0.77rem" }}>
                  {axisTicks.map((tickValue, index) => {
                    const textAlign = index === 0 ? "left" : index === axisTicks.length - 1 ? "right" : "center";
                    return (
                      <Typography key={`${tickValue}-${index}`} sx={{ textAlign, fontSize: "0.77rem" }}>
                        {formatCompact(tickValue)}
                      </Typography>
                    );
                  })}
                </Box>
                <Box />
              </Box>
            )}

            {assessmentView !== "organization" &&
              activeBars.map((item) => {
                const barWidth = Math.max(2, (item.value / maxValue) * 100);

                return (
                  <Box
                    key={item.name}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr auto",
                      alignItems: "center",
                      columnGap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        minHeight: isLocationScopeView ? 24 : 30,
                        borderRadius: 1,
                        backgroundColor: "#edf1f4",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          bottom: 0,
                          width: `${barWidth}%`,
                          backgroundColor: "#b4e5df",
                        }}
                      />
                      <Tooltip title={item.name} placement="top" arrow>
                        <Typography
                          sx={{
                            position: "relative",
                            zIndex: 1,
                            px: isLocationScopeView ? 0.75 : 1,
                            py: isLocationScopeView ? 0.35 : 0.5,
                            color: "#184269",
                            fontSize: isLocationScopeView ? "0.68rem" : "0.9rem",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item.name}
                        </Typography>
                      </Tooltip>
                    </Box>

                    <Tooltip
                      title={`${item.name}: ${item.value.toLocaleString("en-IN")} (${((item.value / (activeBarsTotal || 1)) * 100).toFixed(1)}%)`}
                      placement="top"
                      arrow
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: "#0f2b52",
                          minWidth: isLocationScopeView ? 72 : 92,
                          textAlign: "right",
                          fontSize: isLocationScopeView ? "0.68rem" : "0.9rem",
                        }}
                      >
                        {item.value.toLocaleString("en-IN")}
                      </Typography>
                    </Tooltip>
                  </Box>
                );
              })}
          </Stack>
        </Box>
      </Stack>
    </Grid>
  );
};
export default EmissionByScopeCategoryBar;
